import { Timestamp } from 'firebase/firestore'
import { RepeatSchedule } from '../shared/calendar.types'

export type ReminderDeliveryMethod = {
  type: 'email' | 'sms'
  target: string
}

export type ReminderPreferences = {
  deliveryMethods: ReminderDeliveryMethod[]
}

export type UserDocument = {
  createdAt: Timestamp
  email: string
  emailVerified: boolean
  name?: string
  personNumber?: string
  pendingInvitations?: {
    businessId: string
    invitationId: string
  }[]
  reminderPreferences?: Record<string, ReminderPreferences>
  selfAssessmentEnabled?: boolean
  signature?: {
    name: string
    imageSrc: string
  }
}

export type Officer = {
  name: string
  role: string
  appointedOn: string
  nationality: string
  occupation: string
  id: string
}

export type Company = {
  title: string
  description: string
  companyNumber: string
  address: string
  dateOfCreation: string
  companyStatus: string
  officers: Array<Officer>
}

export type BusinessVatSettings = {
  vatRegistered: boolean
  vatNumber?: string
}

export type BusinessDocument = {
  business: Company
  owner: string
  calendar: {
    accountingReferenceDate: string
  }
  vatSettings?: BusinessVatSettings
}

export enum WorkspaceType {
  BUSINESS = 'business',
  PERSONAL = 'personal',
}

export type WorkspaceDocument = {
  name: string
  owner: string
  users: string[]
  usersCanManageEvents: string[]
  usersCanManageReminders: string[]
  usersCanManageDocuments: string[]
  collaborators: Record<
    string,
    {
      name: string
      email: string
      permissions: CollaboratorPermissions
    }
  >
  calendar: {
    yearEndName: string
    yearEndReferenceDate: string
  }
} & (
  | {
      spaceType: WorkspaceType.BUSINESS
      business: { business: Company; vatSettings?: BusinessVatSettings }
    }
  | {
      spaceType: WorkspaceType.PERSONAL
    }
)

export type CollaboratorPermissions = Partial<{
  isOwner: boolean
  canManageEvents: boolean
  canManageReminders: boolean
  canManageDocuments: boolean
}>

export type WorkspaceCollaboratorDocument = {
  userId: string
  addedAt: Timestamp
  permissions: CollaboratorPermissions
}

export type RepeatingEventDocument = {
  name: string
  repeats: RepeatSchedule
  lastGeneratedDate?: string
  reminders?: EventReminderConfig
  isSystemEvent?: boolean
}

export type ReminderSchedule = {
  beforeDate: {
    months?: number
    weeks?: number
    days?: number
  }
}

export type EventReminderConfig = {
  schedules: ReminderSchedule[]
  recipients?: string[]
}

export type CalendarEventAction = {
  id: string
  description: string
  completed: boolean
}

export type CalendarEventDocument = {
  workspaceId: string
  name: string
  date: string
  repeatingEventId?: string
  reminders?: EventReminderConfig
  notes?: string
  actions?: CalendarEventAction[]
  isCompleted?: boolean
}

export type EventReminderStatus =
  | 'created' // Reminder doc created, but not yet scheduled
  | 'pending' // Reminder waiting to be scheduled
  | 'scheduled' // Reminder scheduled
  | 'processing' // Reminder is being processed
  | 'sent' // Reminder sent
  | 'failed' // Reminder failed to send

export type EventReminderDocument = {
  eventId: string
  eventDate: string
  name: string
  reminderDateTime: string
  userId: string
  deliveryMethod: ReminderDeliveryMethod
  status: EventReminderStatus
  statusHistory: {
    status: EventReminderStatus
    timestamp: Timestamp
    error?: string
  }[]
  createdAt: Timestamp
  updatedAt?: Timestamp
  error?: string | null
}

export type InvitationStatus =
  | 'created' // Invitation created, but not yet sent
  | 'pending' // Invitation sent, not yet accepted
  | 'accepted' // User has accepted and joined the business
  | 'expired' // Link has expired
  | 'cancelled' // Business owner cancelled the invitation
  | 'error' // Invitation failed to send

export type WorkspaceInvitationDocument = {
  workspaceId: string
  email: string
  createdBy: string
  createdAt: Timestamp
  expiresAt: Timestamp
  status: InvitationStatus
  permissions: CollaboratorPermissions
  statusHistory: {
    status: InvitationStatus
    timestamp: Timestamp
    userId?: string
    error?: string
  }[]
  acceptedAt?: Timestamp
  acceptedBy?: string
}

export type DocumentMetadata = {
  fileName: string
  mimeType: string
  size: number
  uploadedAt: Timestamp
  uploadedBy: string
  lastModifiedAt: Timestamp
  lastModifiedBy: string
  version?: number
}

export type FiscalPeriod = {
  startDate: string
  endDate: string
}

export type SignedBlockContent =
  | { type: 'text'; text: string }
  | { type: 'image'; imageSrc: string }
  | {
      type: 'date-boxes'
      date: string // ISO format date string
    }

export type DocumentSignatorySignedBlockContent = {
  id: string
  value: SignedBlockContent
  blockType: DocumentSigningTemplateBlockType
  insertedAt: Timestamp
}

export type DocumentSignatorySigned = {
  timestamp: Timestamp
  ipAddress: string
  userAgent: string
  blockContents: Record<string, DocumentSignatorySignedBlockContent>
}

export type DocumentSignatory = {
  id: string
  name: string
  email: string
  order: number
  status: 'pending' | 'signed'
  firstViewedAt?: Timestamp
  viewedAt?: Timestamp
  signed?: DocumentSignatorySigned
}

export type DocumentSignatories = Record<string, DocumentSignatory>

export enum DocumentSigningTemplateBlockType {
  SIGNATURE = 'signature',
  DATE_SIGNED = 'date-signed',
  DATE_SIGNED_BOXES = 'date-signed-boxes',
  NAME = 'name',
}

export type DocumentSigningTemplateBlock = {
  id: string
  type: DocumentSigningTemplateBlockType
  signatoryId: string
  page: number
  position: {
    x: number
    y: number
  }
  size: {
    width: number
    height: number
  }
}

export type DocumentSigningTemplate = {
  blocks: Array<DocumentSigningTemplateBlock>
  pageDimensions: Array<{
    width: number
    height: number
  }>
}

export type DocumentSigningStatus =
  | 'setup' // choosing signatories and creating template
  | 'signing-in-progress' // waiting for signatures
  | 'signing-complete' // everyone has signed, but document not yet prepared
  | 'signed' // document prepared and signed

export type DocumentSigningStatusHistory = {
  status: DocumentSigningStatus
  timestamp: Timestamp
  actorId: string
  reason?: string
}

export type DocumentDocument = {
  name: string
  metadata: DocumentMetadata
  storageRef: string
  workspaceId: string
  fiscalPeriod?: FiscalPeriod | null
  signing?: {
    status: DocumentSigningStatus
    statusHistory: Array<DocumentSigningStatusHistory>
    signatories?: DocumentSignatories
    template?: DocumentSigningTemplate
    currentSignerId?: string
    signedDocumentStorageRef?: string
  }
}
